// Device-local incoming-ringtone choice (web). No server API — the ringtone is
// a per-device preference, must work offline, and is read synchronously the
// instant a call rings, so this is localStorage-backed like storage/favorites.
// The value is a ringtone id from call/ringtones.ts; an unknown/absent id falls
// back to the default at play time (ringtoneById), so a removed tone never
// breaks ringing.

import { DEFAULT_RINGTONE_ID } from '@merkury/brain/call/ringtones.js';

const KEY = 'merkury.ringtone';

type Listener = () => void;
const listeners = new Set<Listener>();
let cache: string | undefined;

function ls(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** Subscribe to ringtone-choice changes (for `useSyncExternalStore`). */
export function subscribeRingtone(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The chosen ringtone id (default when unset). Referentially stable per value. */
export function getRingtoneId(): string {
  if (cache === undefined) cache = ls()?.getItem(KEY) ?? DEFAULT_RINGTONE_ID;
  return cache;
}

export function setRingtoneId(id: string): void {
  if (id === getRingtoneId()) return;
  cache = id;
  ls()?.setItem(KEY, id);
  for (const listener of listeners) listener();
}

/** Test seam: drop the in-memory snapshot so the next read re-hydrates. */
export function resetRingtoneCache(): void {
  cache = undefined;
}
